import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { PaginatedResponse } from '../types/api';
import type { PartnerReferralCode } from '../types/partnerReferralCode';

type PartnerReferralCodesResponse = PaginatedResponse<PartnerReferralCode[]>;

type DefaultError = unknown;

export const partnerReferralCodeQueryKeys = {
  all: ['partnerReferralCodes'] as const,
  lists: () => [...partnerReferralCodeQueryKeys.all, 'list'] as const,
  list: (params: Record<string, any>) =>
    [...partnerReferralCodeQueryKeys.lists(), params] as const,
  details: () => [...partnerReferralCodeQueryKeys.all, 'detail'] as const,
  detail: (id: string) =>
    [...partnerReferralCodeQueryKeys.details(), id] as const,
  subscriptions: (id: string, params: Record<string, any>) =>
    [
      ...partnerReferralCodeQueryKeys.detail(id),
      'subscriptions',
      params,
    ] as const,
};

type UsePartnerReferralCodesParams = {
  searchParams?: Record<string, any>;
  enabled?: boolean;
};

export function usePartnerReferralCodes<
  TData = PartnerReferralCodesResponse,
  TError = DefaultError,
>(
  { searchParams = {}, enabled = true }: UsePartnerReferralCodesParams = {},
  options?: Omit<
    UseQueryOptions<PartnerReferralCodesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = partnerReferralCodeQueryKeys.list(searchParams);

  return useQuery<PartnerReferralCodesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getPartnerReferralCodes(searchParams),
    ...(options as any),
  });
}
